// - - - - - - - - - -
// isMsalAuthResponseWindow — #508 (MSAL v5).
//
// WHY THIS EXISTS
// ---------------
// MSAL v4 finished a popup sign-in by having the MAIN window read the popup's
// URL once Microsoft redirected it back to us. MSAL v5 stopped doing that: the
// page the popup (or the hidden silent-renewal iframe) lands on must itself run
// `broadcastResponseToMainFrame()` from `@azure/msal-browser/redirect-bridge`,
// which hands the result to the main window over a BroadcastChannel.
//
// Our redirect URI is the app root, so without this the popup loads the WHOLE
// Scheduler, nobody broadcasts, and the main window waits forever on "Sign In
// with Microsoft" (measured on UAT 2026-09-28).
//
// ⚠️ HOW IT DECIDES — and why not `window.opener`
// ------------------------------------------------
// The first version asked "am I a child window (opener / parent)?". It passed
// every automated check and then FAILED in Leland's real Chrome: Microsoft's
// sign-in pages can sever `window.opener` (a cross-origin-opener-policy
// browsing-context swap), so the popup looked like a top-level tab, booted the
// app, and showed our own sign-in screen. Reproduced by nulling the opener
// before the app's code runs — identical symptom.
//
// So this keys on what the URL CARRIES, which survives any window handling:
// MSAL's own `state`. MSAL writes it as base64(JSON `{ id, meta }`), optionally
// followed by `|<app state>` — and it is present on every return: sign-in and
// silent renewal (`state` + `code`/`error`) AND sign-out (`state` only; v5
// adds it precisely so the post-logout page can run the bridge). A random or
// app-owned `state` does not decode to that shape, so a normal tab still boots.
//
// Running the bridge in a TOP-LEVEL tab that carries genuine MSAL state is also
// correct: that is a redirect-flow landing, which the bridge handles by
// navigating home. (The app uses popups today; this keeps it safe either way.)
//
// Pure — exported for tests, which import these exact functions.
// - - - - - - - - - -

/**
 * True when `state` is MSAL's library state: base64(JSON) with a string `id`
 * and an object `meta`, optionally followed by `|<user state>`.
 * @param {string} state
 * @returns {boolean}
 */
export function isMsalLibraryState(state) {
  if (typeof state !== "string" || state.length === 0) return false;
  const libraryPart = state.split("|")[0];
  try {
    const b64 = libraryPart.replace(/-/g, "+").replace(/_/g, "/");
    const padded = b64 + "=".repeat((4 - (b64.length % 4)) % 4);
    const decoded = JSON.parse(atob(padded));
    return (
      decoded !== null &&
      typeof decoded === "object" &&
      typeof decoded.id === "string" &&
      decoded.id.length > 0 &&
      decoded.meta !== null &&
      typeof decoded.meta === "object"
    );
  } catch {
    return false;
  }
}

/**
 * @param {Window} win - the window to classify (pass `window`).
 * @returns {boolean} true when this window has just been sent back from
 *   Microsoft carrying an MSAL response (sign-in, silent renewal or sign-out).
 */
export function isMsalAuthResponseWindow(win) {
  if (!win || !win.location) return false;
  const raw = `${win.location.hash || ""}&${(win.location.search || "").replace(/^\?/, "")}`
    .replace(/^#/, "");
  const params = new URLSearchParams(raw.replace(/^&+/, ""));
  return isMsalLibraryState(params.get("state"));
}
